/**
 * Authoritative Context-panel mapping for the verification workspace.
 *
 * There is no production `/api/verifications/by-task/:taskId` controller yet.
 * The live contract is therefore composed from APIs that already own the data:
 * AppTask -> Forge tags -> linked Documents. These helpers keep that mapping
 * explicit and testable instead of recreating the old demo VM in a component.
 */

/**
 * THE VIEW TABLE — six world-fixed ortho views (three opposite pairs) + Iso.
 *
 * NAMING CONVENTION: **the label names the face you see.** `North` puts the
 * camera north of the tag looking south, so the tag's north face is what fills
 * the screen. `Top` looks down, so you see the top face.
 *
 * `name` is the AXIS the camera looks ALONG, in the app's own vocabulary
 * (Eastings = X, Northings = Y, Elevation = Z — same words as the wizard's
 * AXIS_FIELDS and the report). That axis is the view's DEPTH axis, i.e. the one
 * it CANNOT measure. That is the single thing a reader could take backwards, so
 * `sub` spells the slice plane the view DOES capture and both slots render
 * together — "Elevation · Top" with "XY" beside it can only be read one way.
 *
 * `sub` carries the SLICE PLANE id, not a prose descriptor. The XY/XZ/YZ/3D
 * tabs that used to name the planes were deleted when the viewer's two bottom
 * bars were consolidated into one, so the View combo is now the only place the
 * surveyor can see which plane a named view actually cuts. Spelled exactly as
 * `asBuiltDraft.CAPTURE_PLANE_BY_VIEW` spells it so the UI reads the same as
 * the console logs.
 *
 * THIS ARRAY IS ONE HALF OF A PAIR. The labels live here; the camera poses live
 * in `computeViewPose.WORLD_ORTHO_VIEWS`. An id in one and not the other fails
 * SILENTLY — `ViewerStatusBar.applyView` logs "unknown viewId" and the camera
 * simply does not move. Edit both, always.
 *
 * ORDER IS LOAD-BEARING IN ONE RESPECT: `iso` must stay LAST.
 * `ViewerStatusBar.isOrthoDividerAfter` draws the menu divider on the
 * non-iso -> iso transition, and nothing else enforces the position.
 *
 * OPPOSITE VIEWS SHARE A PLANE, AND THAT IS DELIBERATE. Capturing XZ from
 * `north` and then again from `south` is a SAME-PLANE re-pick: it falls through
 * `asBuiltDraft`'s agreement gate straight to `applyContribution` and
 * overwrites. Correct — a mirrored camera down the same axis has exactly the
 * same depth degeneracy, so the second reading is a CORRECTION, not an
 * independent cross-view check. Do not "fix" this into a gate.
 */
export const VERIFICATION_WORKSPACE_VIEWS = Object.freeze([
  Object.freeze({ id: 'top', name: 'Elevation', side: 'Top', sub: 'XY', glyph: 'plan', kbd: '1' }),
  Object.freeze({ id: 'bottom', name: 'Elevation', side: 'Bottom', sub: 'XY', glyph: 'plan-up', kbd: '4' }),
  Object.freeze({ id: 'north', name: 'Northings', side: 'North', sub: 'XZ', glyph: 'elev', kbd: '2' }),
  Object.freeze({ id: 'south', name: 'Northings', side: 'South', sub: 'XZ', glyph: 'elev-rev', kbd: '5' }),
  Object.freeze({ id: 'east', name: 'Eastings', side: 'East', sub: 'YZ', glyph: 'sect', kbd: '3' }),
  Object.freeze({ id: 'west', name: 'Eastings', side: 'West', sub: 'YZ', glyph: 'sect-rev', kbd: '6' }),
  Object.freeze({ id: 'iso', name: 'Isometric', sub: '3D', glyph: 'iso', kbd: '0' }),
]);

/**
 * The one place a view's display label is composed, so the combo button, the
 * menu row and the button's tooltip cannot drift apart. Iso carries no `side`
 * and renders as a bare "Isometric".
 */
export function verificationViewLabel(view) {
  if (!view) return '';
  return view.side ? `${view.name} · ${view.side}` : view.name;
}

/**
 * The views that capture `plane`, in table order — an opposite pair for each of
 * XY / XZ / YZ. Used by the wizard to name BOTH members when it asks for a
 * plane, because with six views "select XZ" no longer identifies one row.
 */
export function verificationViewsForPlane(plane) {
  return VERIFICATION_WORKSPACE_VIEWS.filter((view) => view.sub === plane);
}

export function taskToVerificationContext(task) {
  if (!task) return null;
  const linkedTags = Array.isArray(task.forgeTags) ? task.forgeTags : [];
  const linkedTagIds = Array.isArray(task.forgeTagsIds) && task.forgeTagsIds.length
    ? task.forgeTagsIds
    : linkedTags.map((tag) => tag?.id).filter(Boolean);

  return {
    taskId: task.id,
    taskName: task.name || task.text || task.friendlyId || 'Verification task',
    taskFriendlyId: task.friendlyId || '',
    taskDescription: task.taskDescription || '',
    taskStatus: task.status || null,
    taskType: task.type || null,
    linkedTags,
    linkedTagIds,
    workspace: {
      activeViewId: 'iso',
      views: VERIFICATION_WORKSPACE_VIEWS,
    },
  };
}

export function getPrimaryVerificationTag(state) {
  const primaryId = state.verification?.linkedTagIds?.[0] || null;
  const fullTags = Array.isArray(state.verifiedTags) ? state.verifiedTags : [];
  const taskTags = Array.isArray(state.verification?.linkedTags)
    ? state.verification.linkedTags
    : [];

  return fullTags.find((tag) => tag?.id === primaryId)
    || fullTags[0]
    || taskTags.find((tag) => tag?.id === primaryId)
    || taskTags[0]
    || null;
}

export function tagTypeLabel(tag) {
  if (!tag) return '';
  const discriminator = String(tag.discriminator || '')
    .replace(/^Forge/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2');
  return tag.type
    || tag.equipmentClass
    || tag.structuralRole
    || tag.assetClass
    || discriminator;
}

export function tagLocationLabel(tag) {
  if (!tag) return '';
  const values = [
    tag.area,
    tag.location,
    tag.funcLocation,
    tag.plant,
    tag.unit,
    tag.level,
  ].filter(Boolean);
  return [...new Set(values.map((value) => String(value).trim()).filter(Boolean))].join(' / ');
}

function normalizeReference(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function getTagIsoReference(tag) {
  return tag?.inspectionIsoNumber
    || tag?.isoReference
    || tag?.customAttributes?.isoReference
    || tag?.customAttributes?.inspectionIsoNumber
    || '';
}

export function isIsoDocument(document, tag = null) {
  if (!document) return false;
  const searchable = [
    document.documentType,
    document.documentTitle,
    document.documentNumber,
    document.fileName,
  ].filter(Boolean).join(' ');
  if (/(^|[^a-z])(iso|isometric)([^a-z]|$)/i.test(searchable)) return true;

  const isoReference = normalizeReference(getTagIsoReference(tag));
  if (!isoReference) return false;
  return [document.documentNumber, document.documentTitle, document.fileName]
    .map(normalizeReference)
    .some((value) => value && (value.includes(isoReference) || isoReference.includes(value)));
}

export function findLinkedIsoDocument(documents, tag = null) {
  return (Array.isArray(documents) ? documents : [])
    .find((document) => isIsoDocument(document, tag)) || null;
}

export function documentIdentity(document) {
  return document?.id || document?.documentId || null;
}

export function documentTypeLabel(document) {
  const explicitType = String(document?.documentType || '').trim();
  if (explicitType) return explicitType;

  const fileName = String(document?.fileName || '');
  const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
  return extension ? extension.toUpperCase() : 'Document';
}

/**
 * Merge the independently queried relationships without showing duplicate
 * choices. A file directly linked to the tag belongs to the Tag group even if
 * it is also attached to the task.
 */
export function mergeReferenceDocuments({
  tagDocuments = [],
  taskDocuments = [],
  tagId = null,
} = {}) {
  const merged = [];
  const seen = new Set();
  const taskIds = new Set(taskDocuments.map(documentIdentity).filter(Boolean));

  const append = (document, relation) => {
    const id = documentIdentity(document);
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push({ ...document, verificationRelation: relation });
  };

  tagDocuments.forEach((document) => {
    const linkedTagIds = Array.isArray(document?.forgeTagsIds)
      ? document.forgeTagsIds
      : [];
    const isDirectTagDocument = !!tagId && linkedTagIds.includes(tagId);
    const relation = !isDirectTagDocument && taskIds.has(documentIdentity(document))
      ? 'task'
      : 'tag';
    append(document, relation);
  });
  taskDocuments.forEach((document) => append(document, 'task'));

  return merged;
}

export function toReferenceDocumentOption(document, tag = null) {
  const id = documentIdentity(document);
  const name = document?.documentNumber
    || document?.documentTitle
    || document?.fileName
    || 'Untitled document';
  const detail = document?.documentTitle && document.documentTitle !== name
    ? document.documentTitle
    : document?.fileName && document.fileName !== name
      ? document.fileName
      : '';
  const revision = document?.revisionNumber
    ? `Rev ${document.revisionNumber}`
    : '';

  return {
    id,
    name,
    subtitle: [detail, revision].filter(Boolean).join(' · '),
    type: documentTypeLabel(document),
    isIso: isIsoDocument(document, tag),
    relation: document?.verificationRelation || 'tag',
    document,
  };
}

/**
 * Vuetify 2 recognises `{ header }` and `{ divider: true }` entries inside a
 * select's items array, which lets the menu communicate relationship ownership
 * without duplicating files that belong to both groups.
 */
export function buildReferenceDocumentItems(documents, tag = null) {
  const options = (Array.isArray(documents) ? documents : [])
    .map((document) => toReferenceDocumentOption(document, tag))
    .filter((option) => !!option.id);
  const groups = [
    { relation: 'tag', header: 'Tag documents' },
    { relation: 'task', header: 'Task documents' },
  ];

  return groups.reduce((items, group) => {
    const groupOptions = options.filter((option) => option.relation === group.relation);
    if (!groupOptions.length) return items;
    if (items.length) items.push({ divider: true });
    items.push({ header: group.header });
    items.push(...groupOptions);
    return items;
  }, []);
}

export function toDrawingDisplay(document, tag = null) {
  const isoReference = getTagIsoReference(tag);
  if (!document) {
    return {
      available: false,
      canExpand: false,
      name: isoReference || 'No linked ISO drawing',
      revision: '',
      thumbnailUrl: null,
      documentId: null,
      document: null,
    };
  }

  const documentId = document.id || document.documentId || null;
  return {
    available: true,
    canExpand: !!documentId && !!(document.storageReference || document.url),
    name: document.documentNumber || document.documentTitle || document.fileName || 'ISO drawing',
    title: document.documentTitle || '',
    revision: document.revisionNumber || '',
    thumbnailUrl: document.thumbnailUrl || null,
    documentId,
    document,
  };
}
